// Parts the Reports and Operator screens share (Reports / Ops UX pass 2026-10-21): the
// edge-to-edge frame under the 56 px location header, the body gutters, the document frame of
// a detail page, the Operator crumb, hover actions for list rows and table rows, and the compact
// KPI strip over a table. Composes @companyos/ui only.
import {
  Button,
  buttonVariants,
  cn,
  type LocationCrumb,
  PageFrame,
  StatGrid,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { Link } from "@tanstack/react-router";
import {
  type ComponentProps,
  type ComponentType,
  cloneElement,
  type ReactElement,
  type ReactNode,
} from "react";

type IconType = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

/** The screen: a column that fills the main region (the header, the toolbar, then a body). */
export function OpsFrame({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="ops-frame"
      className={cn("flex min-h-full min-w-0 flex-col", className)}
      {...props}
    />
  );
}

/** The body under the header and toolbar: 24 px gutters (16 on phones), 16 px between blocks. */
export function OpsBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="ops-body"
      className={cn("flex min-w-0 flex-1 flex-col gap-4 px-6 py-4 max-md:px-4", className)}
      {...props}
    />
  );
}

/** The document frame of a detail page (a deploy, a template, a version) under its header. */
export function OpsDoc({ className, ...props }: ComponentProps<"div">) {
  return (
    <PageFrame
      width="doc"
      data-slot="ops-doc"
      className={cn("min-h-0 pt-5", className)}
      {...props}
    />
  );
}

/** "Operator" as the first crumb of every console page, back to Companies. */
export const operatorCrumb: LocationCrumb = {
  label: "Operator",
  render: <Link to="/operator" />,
};

/** The compact KPI strip over a table: `StatTile size="sm"` four across, 12 px apart. */
export function Tiles({ className, ...props }: ComponentProps<typeof StatGrid>) {
  return <StatGrid dense className={cn("mt-0", className)} {...props} />;
}

/** A hover action: a 28 px ghost icon button with a tooltip and an accessible name. */
export function RowAction({
  icon: Icon,
  label,
  tooltip,
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, "children" | "render"> & {
  icon: IconType;
  /** The accessible name (names the row's subject too). */
  label: string;
  /** The tooltip's shorter text, when the name is long. */
  tooltip?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            className={className}
            {...props}
          />
        }
      >
        <Icon aria-hidden={true} />
      </TooltipTrigger>
      <TooltipContent>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A hover action that is a router link (open the company, the DE, the report): the link keeps
 * its link role and wears the icon button's look (`buttonVariants`), so "Open …" is a link to
 * readers and to tests.
 */
export function RowLinkAction({
  icon: Icon,
  label,
  tooltip,
  render,
  className,
}: {
  icon: IconType;
  label: string;
  tooltip?: string;
  /** The `<Link to=… />` element; the button's look is merged onto it. */
  render: ReactElement<{ className?: string; "aria-label"?: string; children?: ReactNode }>;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={cloneElement(render, {
          className: cn(buttonVariants({ variant: "ghost", size: "icon-sm" }), className),
          "aria-label": label,
        })}
      >
        <Icon aria-hidden={true} />
      </TooltipTrigger>
      <TooltipContent>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
}

/** A 28 px ghost button that is a router link (a header action, a toolbar's Review). */
export function LinkButton({
  render,
  variant = "ghost",
  size = "sm",
  className,
  children,
}: {
  render: ReactElement<{ className?: string; children?: ReactNode }>;
  variant?: "ghost" | "secondary" | "default";
  size?: "xs" | "sm";
  className?: string;
  children: ReactNode;
}) {
  return cloneElement(render, {
    className: cn(buttonVariants({ variant, size }), className),
    children,
  });
}

/**
 * The actions cell of a table row: hidden until the row is hovered or focused within, always
 * shown on coarse pointers (the same rule as `ListRow`'s actions). The row carries `group/row`
 * (`TABLE_ROW_HOVER`).
 */
export function RowActions({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      data-slot="row-actions"
      className={cn(
        "flex items-center justify-end gap-0.5 opacity-0 transition-opacity duration-(--dur-fast) group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100",
        className,
      )}
      {...props}
    />
  );
}

/** Class of a `TableRow` whose actions cell is a `RowActions`. */
export const TABLE_ROW_HOVER = "group/row";

/** The muted 12 px fact line under a title in a table's row header. */
export function RowSub({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("text-xs text-muted-foreground", className)} {...props} />;
}
